import React, {useEffect, useState} from 'react';
import {useRouteMatch} from 'react-router-dom';

import {DisclaimerModal, ModalDisclaimersService} from '@vw-marketing/us-components';
import {InteractionLayerSizeV2, LayerCloseHandleV2, LayerRenderOptionsV2} from '@volkswagen-onehub/layer-manager';
import {useApolloClient} from '@apollo/client';

import {AppStatus, DisclaimerResults} from '../../typing/main';
import {BROCHURES_CONFIG} from './helpers';
import {DEALER_FORM_CONFIGURATION, FORM_CONFIGURATION, MAPS_URL_TEMPLATE_DETAILS} from '../../utils/app-constants';
import {DetailsHeader} from '../../components/details-header';
import {DetailsTitle} from '../../components/details-title';
import {ErrorMessage} from '../../components/error-message';
import {EstimatePayment} from '../../components/estimate-payment/EstimatePayment';
import {getDisclaimerKeys} from '../../utils/general';
import {getStringifiedServiceConfigsServiceConfig} from '../../utils/get-stringified-service-configs-service-config';
import {globalIsSticky, modalsData} from '../../store/local-storage';
import {ImageGallery} from '../../components/image-gallery';
import {LayerContentState, formType} from '../../typing/forms';
import {LoaderSpinner} from '../../components/loader-spinner';
import {Match, MatchCPODetails} from '../../typing/router-types';
import {ModalDisplayMode} from '../../typing/modal-display';
import {ModalForm} from '../../components/modal-form';
import {onHandleBasicFormButtonLink} from '../../utils/tracking-helpers';
import {onVWBasicVehicleButtonClick, onVWBasicVehiclePageload} from '../../tagging/tagging-helpers';
import {scrollToDocumentTop} from '../../utils/general';
import {SimilarVehicles} from '../../components/similar-vehicles';
import {StyledDetailsMainContainer, StyledDetailsStickyContainer, StyledLandingCarDetails, StyledLandingCarDetailsError, StyledLandingCarDetailsLeft, StyledLandingCarDetailsRight} from './styles';
import {USED_VEHICLE} from '../../store/queries/details-vehicle';
import {useDisclaimerModule} from '../../hooks/use-disclaimer-module';
import {UsedVehicle} from '../../typing/used-inventory';
import {useFeatureAppConfig} from '../../hooks/use-feature-app-config';
import {useFeatureServices} from '../../hooks/use-feature-services';
import {useTrackingManager} from '../../hooks/use-tracking-manager';
import {useTranslations} from '../../hooks/use-translations';
import {useZipData} from '../../hooks/use-zip-data';
import Brochures from '../../components/brochures';
import BuyingProcess from '../../components/buying-process';
import CarDetailedSpecs from '../../components/car-detailed-specs';
import CarFax from '../../components/car-fax';
import DealerDetails from '../../components/dealer-details';
import StickyScrollContainer from '../../components/sticky-scroll-container';

export const renderLayerContent = (
  state: LayerContentState,
  close: LayerCloseHandleV2<void>,
  options: LayerRenderOptionsV2,
) => <ModalForm state={state} close={close} options={options} />;

const UsedVehicleDetailPage: React.FC = () => {
  const [usedVehicleData, setUsedVehicleData] = useState<UsedVehicle>({});
  const [fetchStatus, setFetchStatus] = useState<AppStatus>('loading');
  const {makeOffer, contactDealer} = useTranslations().modals;
  const {
    opeFAName = '',
    showPaymentEstimatorCTA,
    faServicesUrl,
    mockIds,
    disclaimers,
    faHostServerUrl,
  } = useFeatureAppConfig();
  const {zipData} = useZipData();
  const client = useApolloClient();
  const layerManager = useFeatureServices()['layer-manager'];
  const match = useRouteMatch();
  const vin = (match as Match<MatchCPODetails>).params.vin;
  const {
    'service-config-provider': serviceConfigProvider,
    's2:server-request': serverRequest,
    'featureapp-service': featureAppService,
  } = useFeatureServices();
  const featureAppConfiguration =
    featureAppService?.featureAppConfigurations[opeFAName];
  const trackingManager = useTrackingManager();
  const serviceConfigsServiceConfig = getStringifiedServiceConfigsServiceConfig(
    serviceConfigProvider,
    serverRequest,
  );

  const modalDisclaimers = getDisclaimerKeys(disclaimers, useDisclaimerModule);
  const priceDisclaimer = modalDisclaimers.find(
    disclaimer => disclaimer.token === 'price',
  );
  const warrantyDisclaimers = modalDisclaimers.filter(
    disclaimer => disclaimer.token === 'warranty',
  );

  const isEstimatyePayment: boolean =
    featureAppConfiguration && showPaymentEstimatorCTA && usedVehicleData.vin
      ? true
      : false;

  const getWarrantyDisclaimerNumber = (year?: string) => {
    if (!year) return;
    let number = '';
    warrantyDisclaimers.map((disclaimer: DisclaimerResults) => {
      if (disclaimer.includes.includes(year)) {
        number = disclaimer.number || '';
      }
    });
    return number;
  };
  const queryVariables = {
    zipcode: zipData.zip,
    vin: vin,
    mapsUrlTemplate: MAPS_URL_TEMPLATE_DETAILS,
  };

  const getVehicleData = async () => {
    const vehicleData = await client.query({
      query: USED_VEHICLE,
      variables: queryVariables,
    });
    return vehicleData?.data;
  };

  const onHandleModelStatus = (formType: formType) => {
    layerManager.openInteractionLayer(
      renderLayerContent,
      {
        text: 'details interaction layer',
        index: 3,
        vehicle: usedVehicleData,
        formType,
        formTranslation: formType === 'make-offer' ? makeOffer : contactDealer,
        zipData,
        serverConfig: {
          faServicesUrl,
          mockIds,
          serviceConfigsServiceConfig,
          trackingManager,
          faHostServerUrl,
        },
      },
      {size: InteractionLayerSizeV2.C},
    );
    onHandleBasicFormButtonLink(
      trackingManager,
      formType === 'contact-dealer'
        ? DEALER_FORM_CONFIGURATION
        : FORM_CONFIGURATION,
      usedVehicleData,
    );
    // handleTagging(formType);
  };

  const onHandleBuyingTransition = (
    vehicle: UsedVehicle,
    vehiclePosition: number,
  ) => {
    const filter = {url: window.location.href, name: 'Build my Deal'};
    onVWBasicVehicleButtonClick(vehicle, filter, trackingManager);
    modalsData({
      vehicle,
      active: true,
      mode: ModalDisplayMode.HANDOFF_BUYING,
      vehiclePosition,
    });
  };

  useEffect(() => {
    if (fetchStatus === 'loading') {
      scrollToDocumentTop();
    }
  }, [fetchStatus]);

  useEffect(() => {
    fetchStatus !== 'loading' && setFetchStatus('loading');
    getVehicleData()
      .then(data => {
        setUsedVehicleData(data?.usedVehicle);
        setFetchStatus('loaded');
        if (data?.usedVehicle) {
          trackingManager &&
            onVWBasicVehiclePageload(data?.usedVehicle, trackingManager);
        }
      })
      .catch(error => {
        console.error('Error loading data:', error.message);
        setFetchStatus('error');
      })
      .finally(() => {
        scrollToDocumentTop();
        globalIsSticky(false);
      });
  }, [vin]);

  if (fetchStatus === 'loading')
    return (
      <StyledLandingCarDetails className="car-details-landing">
        <LoaderSpinner />
      </StyledLandingCarDetails>
    );

  if (fetchStatus === 'error')
    return (
      <StyledLandingCarDetailsError className="car-details-landing-error">
        <ErrorMessage message1={'Error loading vehicle details data'} />
      </StyledLandingCarDetailsError>
    );

  return (
    <ModalDisclaimersService disclaimerData={{active: false}}>
      <StyledDetailsMainContainer className="car-details_main-container">
        <StyledDetailsStickyContainer className="car-details_sticky-wrapper">
          <StickyScrollContainer>
            <DetailsHeader
              vehicle={usedVehicleData}
              onHandleContactDealer={onHandleModelStatus.bind(
                null,
                'contact-dealer',
              )}
              onHandleMakeOffer={onHandleModelStatus.bind(null, 'make-offer')}
              startBuyingHandler={onHandleBuyingTransition.bind(
                null,
                usedVehicleData,
                1,
              )}
            />
          </StickyScrollContainer>
        </StyledDetailsStickyContainer>
        <StyledLandingCarDetails className="car-details">
          <StyledLandingCarDetailsLeft className="car-details-left">
            <DetailsTitle vehicle={usedVehicleData} />
            <ImageGallery vehicle={usedVehicleData} />
            {BROCHURES_CONFIG && (
              <Brochures
                brochuresData={BROCHURES_CONFIG}
                vehicleData={usedVehicleData}
              />
            )}
            <CarDetailedSpecs vehicle={usedVehicleData} />
          </StyledLandingCarDetailsLeft>
          <StyledLandingCarDetailsRight
            className="car-details-right"
            isEstimatyePayment={isEstimatyePayment}
          >
            <BuyingProcess
              vehicle={usedVehicleData}
              onHandleMakeOffer={onHandleModelStatus.bind(null, 'make-offer')}
              zipcode={zipData.zip}
            />
            <DealerDetails
              vehicle={usedVehicleData}
              onHandleContactDealer={onHandleModelStatus.bind(
                null,
                'contact-dealer',
              )}
            />
            <CarFax vehicle={usedVehicleData} />
            {isEstimatyePayment && (
              <EstimatePayment
                vehicle={usedVehicleData}
                zipcode={zipData.zip}
              />
            )}
          </StyledLandingCarDetailsRight>
        </StyledLandingCarDetails>
        <StyledLandingCarDetails className="similar-vehicles-section">
          <SimilarVehicles
            modelNames={usedVehicleData?.model ? [usedVehicleData?.model] : []}
            zipcode={zipData.zip}
            priceDisclaimer={priceDisclaimer?.number}
            getWarrantyDisclaimer={getWarrantyDisclaimerNumber}
          />
        </StyledLandingCarDetails>
        <DisclaimerModal disclaimers={modalDisclaimers} />
      </StyledDetailsMainContainer>
    </ModalDisclaimersService>
  );
};

export default UsedVehicleDetailPage;
